import { Box, Button, Stack, Text } from "@chakra-ui/react";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { FiArrowRight, FiXCircle } from "react-icons/fi";
import type { BuyerBidV2DetailDto } from "../../../../autogen/bff-api";
import { useDisplayDateWithTime } from "../../../../common/formatting/useDisplayDateWithTime";
import { RejectionDetails } from "../../../../common/rejection/RejectionDetails";
import { SectionCard } from "../../../sourcing-events/portal/view-single/SectionCard";

export const RejectedSummary = ({
  detail,
  onViewDetails,
}: {
  detail: BuyerBidV2DetailDto;
  onViewDetails?: () => void;
}) => {
  const { t } = useTranslation();
  const displayDateTime = useDisplayDateWithTime();
  const rejection = detail.bid.rejection;
  const rejectedMeta = formatRejectedMeta({
    rejectedAt: rejection?.rejectedAt,
    rejectedBy: rejection?.rejectedBy,
    displayDateTime,
    t,
  });

  return (
    <SectionCard
      icon={FiXCircle}
      title={t("Avvist leverandør")}
      borderColor="red.100"
      headerBg="red.50"
      headerBorderColor="red.100"
      iconColor="red.600"
    >
      <Stack spacing="3">
        {rejectedMeta ? (
          <Text fontSize="sm" color="smMuted">
            {rejectedMeta}
          </Text>
        ) : null}

        {rejection?.grounds?.length ? (
          <Text fontSize="sm" color="smText">
            {t("{{count}} lovhjemmel valgt", {
              count: rejection.grounds.length,
            })}
          </Text>
        ) : (
          <Text fontSize="sm" color="smMuted">
            {t("Det er ikke lagret detaljer for denne avvisningen.")}
          </Text>
        )}

        {onViewDetails ? (
          <Button
            alignSelf="flex-start"
            size="sm"
            variant="link"
            colorScheme="teal"
            rightIcon={<FiArrowRight />}
            onClick={onViewDetails}
          >
            {t("Se avvisningsdetaljer")}
          </Button>
        ) : null}
      </Stack>
    </SectionCard>
  );
};

export const RejectionDetailsSection = ({
  detail,
}: {
  detail: BuyerBidV2DetailDto;
}) => {
  const { t } = useTranslation();
  const displayDateTime = useDisplayDateWithTime();
  const rejection = detail.bid.rejection;
  const rejectedMeta = formatRejectedMeta({
    rejectedAt: rejection?.rejectedAt,
    rejectedBy: rejection?.rejectedBy,
    displayDateTime,
    t,
  });

  if (!rejection) return null;

  return (
    <Box id="bid-rejection-details" scrollMarginTop="6">
      <SectionCard icon={FiXCircle} title={t("Avvisningsdetaljer")}>
        <Stack spacing="3">
          {rejectedMeta ? (
            <Text fontSize="sm" color="smMuted">
              {rejectedMeta}
            </Text>
          ) : null}
          <RejectionDetails
            grounds={rejection.grounds}
            reason={rejection.reason}
            message={rejection.message}
            attachments={rejection.attachments}
            attachmentIds={rejection.attachmentIds}
          />
        </Stack>
      </SectionCard>
    </Box>
  );
};

const formatRejectedMeta = ({
  rejectedAt,
  rejectedBy,
  displayDateTime,
  t,
}: {
  rejectedAt?: string | null;
  rejectedBy?: string | null;
  displayDateTime: (value: string) => string;
  t: TFunction;
}) => {
  if (rejectedAt && rejectedBy) {
    return t("Avvist {{date}} av {{person}}", {
      date: displayDateTime(rejectedAt),
      person: rejectedBy,
    });
  }
  if (rejectedAt) {
    return t("Avvist {{date}}", { date: displayDateTime(rejectedAt) });
  }
  if (rejectedBy) {
    return t("Avvist av {{person}}", { person: rejectedBy });
  }
  return null;
};
